import { useCallback } from "react";
import { Bill } from "@common/types/bill";
import { TaskType } from "@common/types/task";
import { BillView } from "@common/types/views/billViews";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    buildBulkBillsTaskView,
    buildFilteredBillsTaskView,
} from "@components/tasks/TaskBody/Bills/billsTaskView";
import { useOverlays } from "contexts/OverlayContext";
import type { BillViewFilters } from "contexts/BillsViewContext";

interface UseBillsBulkCreateTaskParams {
    view: BillView;
}

interface BillRef {
    id: string;
    entityId: number;
}

/**
 * Opens the create task drawer for a BILLS task from the bills page: either
 * pinned to the selected bills, or covering every bill matching the page
 * filters ("select all").
 */
export function useBillsBulkCreateTask({ view }: UseBillsBulkCreateTaskParams) {
    const { openOverlay } = useOverlays();
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const createTaskFromBills = useCallback(
        (bills: ReadonlyArray<BillRef | Bill>) => {
            // The create form can only be refused by the server on save.
            if (refuseIfViewOnly() || bills.length === 0) {
                return;
            }
            const entityIds = [...new Set(bills.map((bill) => bill.entityId))];
            openOverlay("createTask", {
                type: TaskType.BILLS,
                viewConfig: buildBulkBillsTaskView({
                    view,
                    billIds: bills.map((bill) => bill.id),
                    entityIds,
                }),
                initialValues: { entityIds },
            });
        },
        [openOverlay, refuseIfViewOnly, view],
    );

    const createTaskFromFilters = useCallback(
        (filters: BillViewFilters | undefined) => {
            if (refuseIfViewOnly()) {
                return;
            }
            openOverlay("createTask", {
                type: TaskType.BILLS,
                viewConfig: buildFilteredBillsTaskView({ view, filters }),
                initialValues: { entityIds: filters?.entityIds },
            });
        },
        [openOverlay, refuseIfViewOnly, view],
    );

    return { createTaskFromBills, createTaskFromFilters };
}
